/**
 * Book of the Day Service
 * 
 * Manages daily book rotation for the hero section on Home screen.
 * - Sources candidates from user's daily recommendations or featured books
 * - Persists history to avoid repeating books
 * - Rotates daily based on date
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { PrivateProfileService } from './privateProfileService';
import { BookService } from './bookService';
import { FireBook } from '../models/FirestoreModels';

const STORAGE_KEY = 'book_of_day_history';

interface BookOfDayHistory {
  lastDate: string; // YYYY-MM-DD format
  shownBookIds: string[];
  currentBookId: string | null;
}

interface BookOfDayResult {
  book: FireBook;
  reason: string;
  isPersonalized: boolean;
}

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayString(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

/**
 * Load history from AsyncStorage
 */
async function loadHistory(): Promise<BookOfDayHistory> {
  try {
    const json = await AsyncStorage.getItem(STORAGE_KEY);
    if (!json) {
      return {
        lastDate: '',
        shownBookIds: [],
        currentBookId: null,
      };
    }
    return JSON.parse(json);
  } catch (error) {
    console.error('Error loading book of day history:', error);
    return {
      lastDate: '',
      shownBookIds: [],
      currentBookId: null,
    };
  }
}

/**
 * Save history to AsyncStorage
 */
async function saveHistory(history: BookOfDayHistory): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving book of day history:', error);
  }
}

/**
 * Get the book of the day
 * 
 * Logic:
 * 1. Check if we've already selected a book for today
 * 2. If yes, return it
 * 3. If no, select a new book:
 *    a. Try to get from user's personalized recommendations
 *    b. Filter out books we've already shown
 *    c. If no new personalized books, get from featured books
 * 4. Save the selection and return
 */
export async function getBookOfDay(
  userId: string | null,
  language: string = 'en'
): Promise<BookOfDayResult | null> {
  try {
    const today = getTodayString();
    const history = await loadHistory();

    // If we already have a book for today, try to return it
    if (history.lastDate === today && history.currentBookId) {
      console.log('📅 Using cached book of day:', history.currentBookId);
      
      // Try to load the book details - normalize the ID to strip any existing language code
      let bookId = history.currentBookId;
      // Remove any trailing _en, _es, etc (2-letter language codes)
      bookId = bookId.replace(/_[a-z]{2}$/, '');
      
      console.log('📖 Normalized book ID:', bookId, 'Language:', language);
      
      const result = await BookService.getBookDetail(bookId, language);
      if (result.success && result.data?.book) {
        return {
          book: result.data.book,
          reason: 'Your free book today',
          isPersonalized: history.shownBookIds.length > 0, // Assume personalized if we have history
        };
      }
      
      // If loading failed, fall through to select a new book
      console.warn('⚠️ Failed to load cached book, selecting new one');
    }

    console.log('🔄 Selecting new book of day...');

    // Get candidate books
    let candidates: Array<{ bookId: string; reason: string }> = [];
    let isPersonalized = false;

    // Try personalized recommendations first
    if (userId) {
      const recsResult = await PrivateProfileService.getCurrentRecommendations(userId);
      if (recsResult.success && recsResult.data?.recommendedBooks) {
        candidates = recsResult.data.recommendedBooks.map(rec => {
          // Normalize book ID - remove any existing language code suffix
          let bookId = rec.bookId;
          bookId = bookId.replace(/_[a-z]{2}$/, '');
          return {
            bookId,
            reason: rec.reason || 'Recommended for you',
          };
        });
        isPersonalized = true;
        console.log(`✅ Found ${candidates.length} personalized recommendations`);
      }
    }

    // If no personalized recommendations, use featured books
    if (candidates.length === 0) {
      const featuredResult = await BookService.getFeaturedBooks(language, 20);
      if (featuredResult.success && featuredResult.data) {
        candidates = featuredResult.data.map(book => ({
          bookId: book.id,
          reason: 'Featured book of the day',
        }));
        console.log(`✅ Using ${candidates.length} featured books`);
      }
    }

    // Filter out books we've already shown
    const unseenCandidates = candidates.filter(
      c => !history.shownBookIds.includes(c.bookId)
    );

    console.log(`📚 Candidates: ${candidates.length} total, ${unseenCandidates.length} unseen`);

    // If we've shown all books, reset history and use full list
    const finalCandidates = unseenCandidates.length > 0 ? unseenCandidates : candidates;

    if (unseenCandidates.length === 0 && candidates.length > 0) {
      console.log('🔄 All books shown, resetting history');
      history.shownBookIds = [];
    }

    // Select the first candidate (or random if you prefer)
    if (finalCandidates.length === 0) {
      console.error('❌ No candidate books available');
      return null;
    }

    const selected = finalCandidates[0];

    // Load book details - ensure bookId is normalized
    const normalizedBookId = selected.bookId.replace(/_[a-z]{2}$/, '');
    console.log('📖 Loading book:', normalizedBookId, 'Language:', language);
    
    const bookResult = await BookService.getBookDetail(normalizedBookId, language);
    if (!bookResult.success || !bookResult.data?.book) {
      console.error('❌ Failed to load book details:', normalizedBookId);
      return null;
    }

    // Update history - store normalized book ID
    const newHistory: BookOfDayHistory = {
      lastDate: today,
      shownBookIds: [...history.shownBookIds, normalizedBookId],
      currentBookId: normalizedBookId,
    };
    await saveHistory(newHistory);

    console.log('✅ Book of day selected:', normalizedBookId);

    return {
      book: bookResult.data.book,
      reason: selected.reason,
      isPersonalized,
    };
  } catch (error) {
    console.error('Error getting book of day:', error);
    return null;
  }
}

/**
 * Reset history (useful for testing)
 */
export async function resetBookOfDayHistory(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
    console.log('✅ Book of day history reset');
  } catch (error) {
    console.error('Error resetting book of day history:', error);
  }
}

